import type { ChangeEvent } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useUploadNaverrekenenTemplateMutation } from 'app/hooks/api/admin';

const MAX_FILE_SIZE = 52428800; // 50 MB

export const useNaverrekenenTemplateUploadPage = () => {
  const intl = useIntl();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);

  const { mutateAsync } = useUploadNaverrekenenTemplateMutation();

  const resetState = useCallback(() => {
    setUploadedFile(null);
    setIsConfirmed(false);
    setError(null);
  }, []);

  const handleFileUpload = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;

    setUploadedFile(file);
    setIsConfirmed(false);
    setError(null);
  }, []);

  const handleConfirmationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setIsConfirmed(event.target.checked);
  }, []);

  const handleUploadFile = useCallback(async () => {
    if (!uploadedFile) return;

    setIsLoading(true);
    setError(null);

    try {
      await mutateAsync(uploadedFile);
      setShowSuccessDialog(true);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        intl.formatMessage({ id: 'tools.naverrekenenTemplate.uploadFailed' });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [uploadedFile, mutateAsync, intl]);

  const handleSuccessDialogClose = useCallback(() => {
    setShowSuccessDialog(false);
    resetState();
  }, [resetState]);

  const isUploadReady = useMemo(
    () => !!uploadedFile && isConfirmed && !isLoading,
    [uploadedFile, isConfirmed, isLoading],
  );

  return {
    MAX_FILE_SIZE,
    uploadedFile,
    isConfirmed,
    isLoading,
    error,
    showSuccessDialog,
    isUploadReady,
    handleFileUpload,
    handleConfirmationChange,
    handleUploadFile,
    handleSuccessDialogClose,
  };
};
